import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Alert } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import { stripeService } from '../../services/stripeService';
import { useTranslation } from 'react-i18next';
import styles from './StripeCheckoutForm.module.css';

interface StripeCheckoutFormProps {
  amount: number;
  onSuccess: (paymentIntentId: string, id:number) => void;
  onError: (error: any) => void;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({ amount, onSuccess, onError }) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();

  const handlePayment = async () => {
    if (!stripe || !elements) {
      console.error("DEBUG : Stripe.js not loaded yet");
      // Stripe.js n'a pas encore chargé
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Créer un PaymentIntent via notre API
      const { clientSecret, id, paymentId } = await stripeService.createPaymentIntent(amount);

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('CardElement not found');
      }

      const paymentResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            // Ces détails peuvent être collectés depuis le formulaire
            name: 'Utilisateur Agence',
          },
        },
      });

      if (paymentResult.error) {
        setError(paymentResult.error.message || t('stripePayment.paymentError'));
        onError(paymentResult.error);
      } else if (paymentResult.paymentIntent && paymentResult.paymentIntent.status === 'succeeded') {
        //stripeService.completeOrder(paymentResult.paymentIntent.id, id, '');
        onSuccess(paymentResult.paymentIntent.id, paymentId);
      }
    } catch (err: any) {
      setError(err.message || t('stripePayment.paymentError'));
      onError(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div className={styles.cardElementContainer}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && (
        <Alert
          message={t('stripePayment.errorTitle')}
          description={error}
          type="error"
          className={styles.errorAlert}
          showIcon
        />
      )}

      <Button
        type="primary"
        onClick={handlePayment}
        disabled={!stripe || processing}
        loading={processing}
        icon={<CreditCardOutlined />}
        className={styles.paymentButton}
        block
      >
        {t('stripePayment.payButton', { amount: amount?.toFixed(2) })}
      </Button>
    </div>
  );
};

export default StripeCheckoutForm;
